import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './BusinessDetailPage.css';

function BusinessDetailPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchBusiness = async () => {
    try {
      const docRef = doc(db, 'business', businessId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setBusiness(data);
        
        // Check if in favorites
        const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
        setIsFavorite(favorites.some(fav => fav.id === docSnap.id));
      } else {
        console.error('Business not found:', businessId);
      }
    } catch (error) {
      console.error('Error fetching business:', error);
    } finally {
      setLoading(false);
    }
    };
    fetchBusiness();
  }, [businessId]);

  return (
    <div className="business-detail-page">
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading business details...</p>
        </div>
      ) : !business ? (
        <div className="not-found">
          <div className="not-found-icon">😞</div>
          <h2>Business not found</h2>
          <p>The business you're looking for doesn't exist or has been removed.</p>
          <Link to="/categories" className="btn btn-primary">Back to Categories</Link>
        </div>
      ) : (
        <>
          <div className="business-detail-header">
            <Link to={`/category/${business.categoryId}`} className="back-link">
              ← Back to category
            </Link>
            <button
              className={`favorite-toggle-btn ${isFavorite ? 'active' : ''}`}
              onClick={() => {
                const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
                if (isFavorite) {
                  const updated = favorites.filter(fav => fav.id !== business.id);
                  localStorage.setItem('favorites', JSON.stringify(updated));
                  setIsFavorite(false);
                } else {
                  favorites.push(business);
                  localStorage.setItem('favorites', JSON.stringify(favorites));
                  setIsFavorite(true);
                }
                window.dispatchEvent(new Event('favoritesUpdated'));
              }}
            >
              {isFavorite ? '❤️' : '🤍'} {isFavorite ? 'Saved' : 'Save'}
            </button>
          </div>

          {/* Image Gallery */}
          {business.imageUrls && business.imageUrls.length > 0 && (
            <div className="business-gallery">
              <img 
                src={business.imageUrls[selectedImage]} 
                alt={business.name} 
                className="main-image" 
              />
              {business.imageUrls.length > 1 && (
                <div className="gallery-thumbnails">
                  {business.imageUrls.map((img, index) => (
                    <img 
                      key={index} 
                      src={img} 
                      alt={`${business.name} ${index + 1}`} 
                      className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                    />
                  ))}
                </div>
              )}
              <div className="image-counter">
                {selectedImage + 1} / {business.imageUrls.length}
              </div>
            </div>
          )}

          <div className="business-header">
            <div className="title-section">
              {business.logoUrl && (
                <img src={business.logoUrl} alt={`${business.name} logo`} className="business-logo" />
              )}
              <div>
                <h1>{business.name}</h1>
                <div className="business-meta">
                  {business.subcategoryId && (
                    <span className="category-badge">{business.subcategoryId}</span>
                  )}
                  {business.rating !== undefined && typeof business.rating === 'number' && (
                    <div className="rating-display">
                      <span className="stars">
                        {'⭐'.repeat(Math.round(business.rating))}
                        {'☆'.repeat(5 - Math.round(business.rating))}
                      </span>
                      <span className="rating-text">
                        {business.rating.toFixed(1)} ({business.reviews?.length || 0} reviews)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="business-content">
            {/* About Section */}
            <div className="info-card">
              <h2>About</h2>
              <p>{business.description || 'No description available.'}</p>
            </div>

            {/* Contact Information */}
            <div className="info-card">
              <h2>Contact Information</h2>
              <div className="contact-grid">
                {business.location && (
                  <div className="contact-item">
                    <span className="contact-icon">📍</span>
                    <div>
                      <strong>Location</strong>
                      <p>{business.location}</p>
                    </div>
                  </div>
                )}
                {business.phone && (
                  <div className="contact-item">
                    <span className="contact-icon">📞</span>
                    <div>
                      <strong>Phone</strong>
                      <p><a href={`tel:${business.phone}`}>{business.phone}</a></p>
                    </div>
                  </div>
                )}
                {business.email && (
                  <div className="contact-item">
                    <span className="contact-icon">📧</span>
                    <div>
                      <strong>Email</strong>
                      <p><a href={`mailto:${business.email}`}>{business.email}</a></p>
                    </div>
                  </div>
                )}
                {business.website && (
                  <div className="contact-item">
                    <span className="contact-icon">🌐</span>
                    <div>
                      <strong>Website</strong>
                      <p><a href={business.website} target="_blank" rel="noopener noreferrer">{business.website}</a></p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Opening Hours */}
            {business.openingHours && Array.isArray(business.openingHours) && business.openingHours.length > 0 && (
              <div className="info-card">
                <h2>Opening Hours</h2>
                <div className="hours-list">
                  {business.openingHours.map((hours, index) => (
                    <div key={index} className="hours-item">
                      <strong>{hours.day}:</strong>
                      <span>
                        {hours.closed ? 'Closed' : `${hours.from} - ${hours.to}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Info */}
            {(business.services || business.facilities) && (
              <div className="info-card">
                <h2>Additional Information</h2>
                {business.services && (
                  <div>
                    <strong>Services:</strong>
                    <p>{business.services}</p>
                  </div>
                )}
                {business.facilities && (
                  <div>
                    <strong>Facilities:</strong>
                    <p>{business.facilities}</p>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Section */}
            <div className="info-card reviews-section">
              <div className="reviews-header">
                <h2>Reviews & Ratings</h2>
                {business.rating !== undefined && typeof business.rating === 'number' && (
                  <div className="overall-rating">
                    <div className="rating-number">{business.rating.toFixed(1)}</div>
                    <div className="rating-stars">
                      {'⭐'.repeat(Math.round(business.rating))}
                    </div>
                    <div className="rating-count">{business.reviews?.length || 0} reviews</div>
                  </div>
                )}
              </div>

              {business.reviews && business.reviews.length > 0 ? (
                <div className="reviews-list">
                  {business.reviews.map((review, index) => (
                    <div key={index} className="review-item">
                      <div className="review-header">
                        <div className="reviewer-info">
                          {review.profilePic ? (
                            <img src={review.profilePic} alt={review.name} className="reviewer-avatar" />
                          ) : (
                            <div className="reviewer-avatar-placeholder">
                              {review.name?.charAt(0) || 'A'}
                            </div>
                          )}
                          <div>
                            <div className="reviewer-name">{review.name || 'Anonymous'}</div>
                            <div className="review-date">{review.date || 'No date'}</div>
                          </div>
                        </div>
                        <div className="review-rating">
                          {'⭐'.repeat(parseInt(review.rating) || 0)}
                        </div>
                      </div>
                      <p className="review-text">{review.review || review.reviewText}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-reviews">
                  <div className="no-reviews-icon">💬</div>
                  <p>No reviews yet</p>
                  <p className="no-reviews-subtitle">Be the first to share your experience!</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BusinessDetailPage;
